import { useEffect, useState } from "react";
import { School, HeartHandshake, Users, Bell, PoundSterling, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

export default function SuperDashboard() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="font-display font-bold animate-pulse" data-testid="super-overview-loading">Loading…</div>;

  const cards = [
    { label: "Schools", value: data.schools, icon: School, bg: "#A7E4D3" },
    { label: "Parents", value: data.parents, icon: HeartHandshake, bg: "#F9A8D4" },
    { label: "Pupils", value: data.pupils, icon: Users, bg: "#7DD3FC" },
    { label: "Notices sent", value: data.notices, icon: Bell, bg: "#D8B4FE" },
    { label: "Total revenue", value: `£${data.revenue_gbp.toFixed(2)}`, icon: PoundSterling, bg: "#FDE68A" },
    { label: "MRR", value: `£${(data.mrr_gbp ?? 0).toFixed(2)}`, icon: TrendingUp, bg: "#A7E4D3" },
    { label: "ARR", value: `£${(data.arr_gbp ?? 0).toFixed(2)}`, icon: TrendingUp, bg: "#7DD3FC" },
    { label: "One-off plans", value: `£${(data.oneoff_gbp ?? 0).toFixed(2)}`, icon: PoundSterling, bg: "#F9A8D4" },
  ];

  return (
    <div data-testid="super-dashboard">
      <span className="nb-label">Platform</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Overview</h1>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <div key={c.label} className="nb-card p-5" data-testid={`super-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-10 h-10 rounded-xl border-2 border-zinc-900 flex items-center justify-center mb-3" style={{ backgroundColor: c.bg }}>
              <c.icon className="w-5 h-5" />
            </div>
            <div className="font-display font-black text-3xl">{c.value}</div>
            <div className="text-sm font-bold text-zinc-500">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 nb-card p-6">
        <h2 className="font-display font-bold text-xl">Recent payments</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse" data-testid="recent-payments-table">
            <thead>
              <tr className="bg-zinc-900 text-white text-left">
                <th className="p-3 text-xs font-bold tracking-wider uppercase">Plan</th>
                <th className="p-3 text-xs font-bold tracking-wider uppercase">Amount</th>
                <th className="p-3 text-xs font-bold tracking-wider uppercase">Status</th>
                <th className="p-3 text-xs font-bold tracking-wider uppercase">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_payments.map((p) => (
                <tr key={p.session_id} className="border-t-2 border-zinc-900" data-testid={`payment-row-${p.session_id}`}>
                  <td className="p-3 font-bold">{p.plan_name || "—"}</td>
                  <td className="p-3">£{(p.amount || 0).toFixed(2)} {String(p.currency || "").toUpperCase()}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${p.payment_status === "paid" ? "bg-[#A7E4D3]" : p.payment_status === "pending" ? "bg-[#FDE68A]" : "bg-red-200"}`}>
                      {p.payment_status}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-zinc-500">{new Date(p.created_at).toLocaleString("en-GB")}</td>
                </tr>
              ))}
              {data.recent_payments.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-zinc-500 text-sm border-t-2 border-zinc-900" data-testid="payments-empty">No payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 nb-card p-6">
        <h2 className="font-display font-bold text-xl">Recent admin actions</h2>
        <div className="mt-4 space-y-2" data-testid="audit-list">
          {audit.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border-2 border-zinc-200 p-3" data-testid={`audit-row-${a.id}`}>
              <div className="min-w-0">
                <span className="font-bold text-sm">{a.action}</span>
                <span className="text-xs text-zinc-500 ml-2">{a.detail || a.target}</span>
              </div>
              <span className="text-xs text-zinc-400 shrink-0">{new Date(a.created_at).toLocaleString("en-GB")}</span>
            </div>
          ))}
          {audit.length === 0 && <p className="text-sm text-zinc-400 font-semibold" data-testid="audit-empty">No admin actions logged yet.</p>}
        </div>
      </div>
    </div>
  );
}
